import { ref, onMounted } from "vue";
import { f7 } from "framework7-vue";
import { openAdaptiveModal, closeAdaptiveModal } from "./useAdaptiveModal.js";
import { utils } from "framework7";
import { Capacitor } from "@capacitor/core";
import { Directory, Filesystem } from "@capacitor/filesystem";
import "@capgo/camera-preview";
import {
  Camera,
  CameraDirection,
  CameraResultType,
  CameraSource,
} from "@capacitor/camera";
import { CameraPreview } from "@capgo/camera-preview";
import { useAuthStore } from "../pinia-stores/auth.js";
import i18n from "../i18n.js";

export function useCordovaCamera({ setAttachment }) {
  const t = i18n.global.t;

  const isCameraOpen = ref(false);
  const isMediaRecording = ref(false);
  const flashModes = ref([]);
  const flashMode = ref("");
  const currentFlashModeIndex = ref(0);
  const cameras = ref([]);
  const cameraIndex = ref(0);
  const cameraId = ref("");
  const videoElement = ref(null);
  const mediaStream = ref(null);
  const currentMediaRecorder = ref(null);
  const torchSupported = ref(false);
  const torchActive = ref(false);
  const constraints = ref({});

  async function openPreviewCamera() {
    openAdaptiveModal(".recordVideoTakePicture", true);
    startWebVideoCamera();
    getDevices();
  }

  async function startPreviewRecording() {
    isMediaRecording.value = true;
    await recordWebVideo();
  }

  async function stopPreviewRecording() {
    stopWebCamera();
    closeAdaptiveModal(".recordVideoTakePicture", true);
  }

  async function stopPreviewCamera() {
    await CameraPreview.stop();
    isCameraOpen.value = false;
  }

  function updateCameraCapabilities() {
    if (!Capacitor.isNativePlatform()) {
      return;
    }
    const result = CameraPreview.getSupportedFlashModes();
    console.log("getSupportedFlashModes", result);
    flashModes.value = result.result;
  }

  async function cameraCapture() {
    if (!Capacitor.isNativePlatform()) {
      capTureWebPhoto();
      return;
    }
    const result = await CameraPreview.capture({});
    const fileName = `image_${new Date().getTime()}.webp`;
    const mimeType = "image/webp";
    const response = await fetch(`data:${mimeType};base64,${result.value}`);
    const blob = await response.blob();
    const rawFile = new File([blob], fileName, { type: mimeType });
    setAttachment({ file: rawFile });
  }

  async function switchCamera() {
    switchWebCamera();
  }

  async function setFlashMode() {
    if (!Capacitor.isNativePlatform()) {
      return;
    }
    if (!currentFlashModeIndex.value) currentFlashModeIndex.value = 0;
    if (!flashModes.value || flashModes.value.length === 0) {
      const result = await CameraPreview.getSupportedFlashModes();
      flashModes.value = result.result;
    }
    if (!(flashModes.value && flashModes.value.length > 0)) {
      return;
    }
    const mode = flashModes.value[currentFlashModeIndex.value];
    flashMode.value = mode;
    await CameraPreview.setFlashMode({ flashMode: mode });
    currentFlashModeIndex.value =
      (currentFlashModeIndex.value + 1) % flashModes.value.length;
  }

  async function checkCameraPermission() {
    if (Capacitor.isNativePlatform()) {
      const { camera: status } = await Camera.checkPermissions();
      if (status === "granted") return true;

      const { camera: permission } = await Camera.requestPermissions();
      return permission === "granted";
    }

    if (!navigator.mediaDevices?.getUserMedia) {
      return false;
    }

    try {
      const result = await navigator.permissions.query({ name: "camera" });
      switch (result.state) {
        case "granted":
          return true;
        case "denied":
          return false;
        case "prompt":
          showButtonToEnableCamera();
          return false;
      }
    } catch (error) {
      console.error("Camera permission check failed:", error);
      return false;
    }
  }

  function showButtonToEnableCamera() {
    f7.dialog.confirm(
      `<span class="avocash" style="text-transform:lowercase; font-weight: bold">avoÃ‚Â¢ash</span>` +
        " " +
        t("tamtammessage.cameraPermissionrequest"),
      () => {
        checkCameraPermission();
      }
    );
  }

  async function takePhoto() {
    const permission = await checkCameraPermission();
    if (permission) {
      const photo = await Camera.getPhoto({
        quality: 90,
        allowEditing: true,
        source: CameraSource.Camera,
        direction: f7.device.desktop
          ? CameraDirection.Front
          : CameraDirection.Rear,
        resultType: Capacitor.isNativePlatform()
          ? CameraResultType.Uri
          : CameraResultType.webPath || CameraResultType.DataUrl,
        promptLabelCancel: t("common.cancel"),
        promptLabelHeader: t("common.photo"),
        promptLabelPhoto: t("common.fromPhotoLibrary"),
        promptLabelPicture: t("common.takePhoto"),
      });
      console.log("image", photo);
      await convertImageToFile(photo);
    }
  }

  async function selectPhotoOrCamera() {
    const permission = await checkCameraPermission();
    if (permission) {
      const photo = await Camera.getPhoto({
        quality: 90,
        allowEditing: true,
        resultType: Capacitor.isNativePlatform()
          ? CameraResultType.Uri
          : CameraResultType.webPath || CameraResultType.DataUrl,
        source: CameraSource.Prompt,
        direction: f7.device.desktop
          ? CameraDirection.Front
          : CameraDirection.Rear,
        promptLabelCancel: t("common.cancel"),
        promptLabelHeader: t("common.photo"),
        promptLabelPhoto: t("common.fromPhotoLibrary"),
        promptLabelPicture: t("common.takePhoto"),
      });
      await convertImageToFile(photo);
      console.log("image", photo);
    }
  }

  async function pickPhotosFromLibrary() {
    const permission = await checkCameraPermission();
    let images = [];
    if (permission) {
      images = Camera.pickImages({
        quality: 90,
        allowEditing: true,
        resultType: CameraResultType.Uri,
        source: CameraSource.Photos,
        direction: f7.device.desktop
          ? CameraDirection.Front
          : CameraDirection.Rear,
      });
      console.log("pickImages", images);
    } else if (Camera.requestPermissions() === "limited") {
      images = await Camera.pickLimitedLibraryPhotos({
        quality: 90,
        allowEditing: true,
        resultType: CameraResultType.Uri,
        source: CameraSource.Photos,
        direction: f7.device.desktop
          ? CameraDirection.Front
          : CameraDirection.Rear,
      });
      console.log("pickLimitedLibraryPhotos", images);
    }
    for (const image of images) {
      await convertImageToFile(image);
    }
  }

  async function getLimitedLibraryPhotos() {
    const permission = await checkCameraPermission();
    if (permission) {
      const images = await Camera.getLimitedLibraryPhotos({
        quality: 90,
        allowEditing: true,
        resultType: CameraResultType.Uri,
        source: CameraSource.Photos,
        direction: f7.device.desktop
          ? CameraDirection.Front
          : CameraDirection.Rear,
      });
      console.log("pickImages", images);
      for (const image of images) {
        await convertImageToFile(image);
      }
    }
  }

  async function startWebVideoCamera() {
    console.log("videoElement :", document.querySelector("videoRecorder"));
    getDevices();
    constraints.value = {
      video: {
        width: {
          min: 640,
          ideal: 1920,
        },
        height: {
          min: 400,
          ideal: 1080,
        },
        facingMode: f7.device.desktop ? "user" : "environment",
      },
      audio: { echoCancellation: true },
    };
    await navigator.mediaDevices
      .getUserMedia(constraints.value)
      .then((stream) => {
        videoElement.value = document.getElementById("videoRecorder");
        videoElement.value.srcObject = stream;
        mediaStream.value = stream;
        videoElement.value.onloadedmetadata = () => {
          videoElement.value.play();
        };
        console.log("mediaStream :", mediaStream.value);
        updateFlashAvailability();
      })
      .catch((error) => {
        console.error("getUserMedia() error:", error);
      });
  }

  async function recordWebVideo() {
    const stream = mediaStream.value || videoElement.value.srcObject;
    isMediaRecording.value = true;
    const recordedChunks = [];
    const recorder = new MediaRecorder(stream);
    currentMediaRecorder.value = recorder;
    console.log(recorder);
    const mimeType = recorder.mimeType.includes(";")
      ? recorder.mimeType.split(";")[0]
      : recorder.mimeType || "video/mp4";
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) {
        recordedChunks.push(e.data);
      }
      if (recorder.state === "active") {
        recorder.stop();
      }
      console.log(recordedChunks);
    };
    recorder.onstop = () => {
      const blob = new Blob(recordedChunks, {
        type: mimeType,
      });
      console.log("Recording stopped", blob);
      blob.name = `${blob.type.split("/")[0]}-${new Date().toISOString()}.${
        blob.type.split("/")[1]
      }`;
      const file = new File(
        [blob],
        `${mimeType.split("/")[0]}-${Date.now()}.${
          mimeType.split("/")[1] || "mp4"
        }`,
        { type: mimeType }
      );
      setAttachment({ file });
    };
    console.log(recorder);
    recorder.start(200);
  }

  async function capTureWebPhoto() {
    await getDevices();
    const canvas = document.createElement("canvas");
    canvas.width = videoElement.value.videoWidth;
    canvas.height = videoElement.value.videoHeight;
    canvas.getContext("2d").drawImage(videoElement.value, 0, 0);
    const img = canvas.toDataURL("image/webp");
    const blob = await (await fetch(img)).blob();
    blob.name = `img-${new Date().toISOString()}.${blob.type.split("/")[1]}`;
    const file = new File(
      [blob],
      `img_${Date.now()}.${blob.type.split("/")[1] || "jpg"}`,
      { type: blob.type }
    );
    setAttachment({ file });
    console.log(file);
    closeAdaptiveModal(".recordVideoTakePicture", true);
  }

  function stopWebCamera() {
    console.log("currentMediaRecorder", currentMediaRecorder.value);
    console.log("mediaStream", mediaStream.value);
    if (
      currentMediaRecorder.value &&
      currentMediaRecorder.value.state !== "inactive"
    ) {
      currentMediaRecorder.value.stop();
      console.log("Stopping media recorder");
    }
    if (videoElement.value) {
      videoElement.value.pause();
      videoElement.value.srcObject = null;
    }
    if (
      currentMediaRecorder.value &&
      currentMediaRecorder.value.stream &&
      currentMediaRecorder.value.stream.getTracks
    ) {
      currentMediaRecorder.value.stream.getTracks().forEach((track) => {
        if (track.readyState === "live") {
          track.stop();
        }
      });
    } else if (mediaStream.value) {
      mediaStream.value.getTracks().forEach((track) => {
        if (track.readyState === "live") {
          track.stop();
        }
      });
    }
    isCameraOpen.value = false;
    isMediaRecording.value = false;
    mediaStream.value = null;
    currentMediaRecorder.value = null;
  }

  async function getDevices() {
    navigator.mediaDevices.enumerateDevices().then((devices) => {
      cameras.value = devices.filter((device) => device.kind === "videoinput");
      console.log("videoDevices", cameras.value.length);
      cameraIndex.value = cameras.value.length == 1;
      console.log("this.cameras", cameras.value.length);
    });
    const supports = navigator.mediaDevices.getSupportedConstraints();
    console.log(supports);
  }

  function updateFlashAvailability() {
    const track = mediaStream.value.getVideoTracks()[0];

    videoElement.value.addEventListener("loadedmetadata", () => {
      onCapabilitiesReady(track.getCapabilities());
    });

    const onCapabilitiesReady = (capabilities) => {
      if (capabilities.torch || track.getSettings().torch) {
        torchSupported.value = true;
      } else if (
        "fillLightMode" in capabilities &&
        capabilities.fillLightMode.length != 0 &&
        capabilities.fillLightMode != "none"
      ) {
        torchSupported.value = true;
      }
      if (torchSupported.value) {
        const switchButton = document.querySelector(".switch-torch");
        switchButton.addEventListener("click", () => {
          try {
            track.applyConstraints({
              advanced: [
                {
                  torch: (torchActive.value = !torchActive.value),
                },
              ],
            });
          } catch (err) {
            console.error(err);
          }
        });
      } else {
        console.log("No torch found");
      }
    };
  }

  async function switchWebCamera() {
    if (!cameras.value || cameras.value.length === 0) {
      await getDevices();
      if (!cameras.value || cameras.value.length === 0) {
        console.error("No cameras available to switch.");
        return;
      }
    }

    cameraId.value = cameras.value[cameraIndex.value].deviceId;
    cameraIndex.value =
      cameraIndex.value + 1 < cameras.value.length
        ? cameraIndex.value + 1
        : 0;

    constraints.value = {
      video: {
        deviceId: { exact: cameraId.value },
      },
      audio: { echoCancellation: true },
    };

    if (!mediaStream.value) return;

    mediaStream.value.getTracks().forEach((track) => track.stop());
    await new Promise((resolve) => setTimeout(resolve, 100));
    await captureCurrentMedia(constraints.value);
  }

  async function convertImageToFile(imageData) {
    let file = null;

    if (imageData.webPath) {
      const response = await fetch(imageData.webPath);
      const blob = await response.blob();
      const mime_type = blob.type ? blob.type : blob.mimeType;
      file = new File(
        [blob],
        `photo_${Date.now()}.${mime_type.split("/")[1] || "jpg"}`,
        { type: mime_type }
      );
    } else if (imageData.path) {
      const fileSrc = Capacitor.convertFileSrc(imageData.path);
      const response = await fetch(fileSrc);
      const blob = await response.blob();
      const mime_type = blob.type ? blob.type : blob.mimeType;
      file = new File(
        [blob],
        `photo_${Date.now()}.${mime_type.split("/")[1] || "jpg"}`,
        { type: mime_type }
      );
    } else if (imageData.dataUrl) {
      const arr = imageData.dataUrl.split(",");
      const mime = arr[0].match(/:(.*?);/)[1];
      const bstr = atob(arr[1]);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }
      const blob = new Blob([u8arr], { type: mime });
      file = new File(
        [blob],
        `photo_${Date.now()}.${mime.split("/")[1] || "jpg"}`,
        { type: mime }
      );
    }

    if (file) {
      setAttachment({ file });
    }
  }

  return {
    isCameraOpen,
    isMediaRecording,
    flashModes,
    flashMode,
    cameras,
    cameraIndex,
    torchSupported,
    torchActive,
    openPreviewCamera,
    startPreviewRecording,
    stopPreviewRecording,
    stopPreviewCamera,
    updateCameraCapabilities,
    cameraCapture,
    switchCamera,
    setFlashMode,
    checkCameraPermission,
    showButtonToEnableCamera,
    takePhoto,
    selectPhotoOrCamera,
    pickPhotosFromLibrary,
    getLimitedLibraryPhotos,
    startWebVideoCamera,
    recordWebVideo,
    capTureWebPhoto,
    stopWebCamera,
    getDevices,
    updateFlashAvailability,
    switchWebCamera,
    convertImageToFile,
  };
}
